<template>
  <v-dialog :model-value="modelValue" max-width="720" scrollable persistent @update:model-value="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="danger" class="mr-2">mdi-alert</v-icon>
        {{ danger ? "Modifier le danger" : "Signaler un danger" }}
      </v-card-title>
      <v-card-subtitle>{{ section.river }} - {{ section.name }}</v-card-subtitle>
      <v-card-text>
        <v-form ref="form" @submit.prevent="save">
          <v-combobox
            v-model="form.rapid_name"
            :items="rapidSuggestions"
            label="Nom du rapide"
            hint="Choisissez un rapide déjà signalé ou écrivez son nom"
            persistent-hint
            :counter="100"
            :rules="[maxLength(100)]"
            class="mb-3"
          ></v-combobox>

          <v-select
            v-model="form.type"
            :items="dangerTypes"
            item-title="text"
            item-value="value"
            label="Type de danger"
            :rules="[required]"
          ></v-select>
          <v-text-field
            v-if="form.type === 'autre'"
            v-model="form.type_other"
            label="Précisez le type de danger"
            :counter="200"
            :rules="[required, maxLength(200)]"
          ></v-text-field>

          <div class="text-label-large mt-2">Impact</div>
          <v-row no-gutters>
            <v-col v-for="impact in dangerImpacts" :key="impact.value" cols="12" sm="6">
              <v-checkbox
                v-model="form.impacts"
                :value="impact.value"
                :label="impact.text"
                density="compact"
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-radio-group
            v-model="form.can_stop"
            label="Peut-on s'arrêter avant le danger ?"
            inline
            class="mt-4"
            :rules="[required]"
          >
            <v-radio v-for="choice in dangerCanStop" :key="choice.value" :label="choice.text" :value="choice.value">
            </v-radio>
          </v-radio-group>
          <v-text-field
            v-if="form.can_stop === 'autre'"
            v-model="form.can_stop_other"
            label="Précisez"
            :counter="200"
            :rules="[required, maxLength(200)]"
          ></v-text-field>

          <v-textarea
            v-model="form.details"
            label="Information additionnelle"
            auto-grow
            rows="3"
            :counter="2000"
            :rules="[maxLength(2000)]"
          ></v-textarea>

          <v-date-input
            :model-value="isoDateToDate(form.start_date)"
            label="Date de début"
            :max="new Date()"
            :rules="[required]"
            @update:model-value="form.start_date = dateToIsoDate($event)"
          ></v-date-input>

          <div class="text-label-large mt-2 mb-2">Photos ({{ photoCount }}/{{ maxPhotos }})</div>
          <div class="d-flex flex-wrap ga-2 mb-2">
            <div v-for="image in keptImages" :key="image.path" class="photo-thumb">
              <img :src="image.url" alt="" />
              <v-btn icon="mdi-close" size="x-small" class="photo-remove" title="Retirer" @click="removeImage(image)">
              </v-btn>
            </div>
            <div v-for="(file, index) in newFiles" :key="file.preview" class="photo-thumb">
              <img :src="file.preview" alt="" />
              <v-btn icon="mdi-close" size="x-small" class="photo-remove" title="Retirer" @click="removeFile(index)">
              </v-btn>
            </div>
          </div>
          <v-btn
            v-if="photoCount < maxPhotos"
            variant="tonal"
            prepend-icon="mdi-camera-plus"
            @click="$refs.photoInput.click()"
          >
            Ajouter des photos
          </v-btn>
          <input ref="photoInput" type="file" accept="image/*" multiple hidden @change="addFiles" />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" :disabled="saving" @click="close(false)">Annuler</v-btn>
        <v-btn color="primary" variant="flat" :loading="saving" @click="save">
          {{ danger ? "Enregistrer" : "Signaler" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { auth } from "@/firebase";
  import { DangersService } from "@/store/modules/dangers";
  import { dangerCanStop, dangerImpacts, dangerTypes } from "@/store/db/dangers.db";
  import { notify, notifyError } from "@/services/Notification.service";
  import { dateToIsoDate, isoDateToDate } from "@/utils/dates";
  import { DANGER_MAX_PHOTOS } from "@/utils/dangers";

  function emptyForm() {
    return {
      rapid_name: "",
      type: null,
      type_other: "",
      impacts: [],
      can_stop: null,
      can_stop_other: "",
      details: "",
      start_date: dateToIsoDate(new Date()),
    };
  }

  export default {
    props: {
      modelValue: Boolean,
      section: { type: Object, required: true },
      // The danger to edit, or null to report a new one.
      danger: { type: Object, default: null },
      rapidSuggestions: { type: Array, default: () => [] },
    },
    emits: ["update:modelValue", "saved"],
    data() {
      return {
        dangerTypes,
        dangerImpacts,
        dangerCanStop,
        maxPhotos: DANGER_MAX_PHOTOS,
        form: emptyForm(),
        keptImages: [],
        removedImages: [],
        // { file, preview } waiting to be uploaded on save
        newFiles: [],
        saving: false,
      };
    },
    computed: {
      photoCount() {
        return this.keptImages.length + this.newFiles.length;
      },
    },
    watch: {
      modelValue(open) {
        if (open) this.reset();
      },
    },
    beforeUnmount() {
      this.clearNewFiles();
    },
    methods: {
      dateToIsoDate,
      isoDateToDate,
      required(value) {
        return (value !== null && value !== undefined && String(value).trim() !== "") || "Requis";
      },
      maxLength(max) {
        return (value) => !value || String(value).length <= max || `${max} caractères maximum`;
      },
      reset() {
        this.clearNewFiles();
        this.form = this.danger
          ? { ...emptyForm(), ...this.danger, impacts: [...(this.danger.impacts || [])] }
          : emptyForm();
        this.keptImages = this.danger ? [...(this.danger.images || [])] : [];
        this.removedImages = [];
        if (this.$refs.form) this.$refs.form.resetValidation();
      },
      clearNewFiles() {
        this.newFiles.forEach((item) => URL.revokeObjectURL(item.preview));
        this.newFiles = [];
      },
      addFiles(event) {
        const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith("image/"));
        const room = this.maxPhotos - this.photoCount;
        if (files.length > room) {
          notifyError(`Maximum ${this.maxPhotos} photos par danger.`);
        }
        files.slice(0, Math.max(room, 0)).forEach((file) => {
          this.newFiles.push({ file, preview: URL.createObjectURL(file) });
        });
        event.target.value = "";
      },
      removeFile(index) {
        URL.revokeObjectURL(this.newFiles[index].preview);
        this.newFiles.splice(index, 1);
      },
      removeImage(image) {
        this.keptImages = this.keptImages.filter((item) => item.path !== image.path);
        this.removedImages.push(image);
      },
      close(value = false) {
        if (this.saving) return;
        this.$emit("update:modelValue", value);
      },
      async save() {
        const { valid } = await this.$refs.form.validate();
        if (!valid || this.saving) return;

        const user = auth.currentUser;
        if (!user) {
          notifyError("Connectez-vous pour signaler un danger.");
          return;
        }

        this.saving = true;
        const dangerId = this.danger ? this.danger.id : DangersService.actions.newDangerId();
        const uploaded = [];
        try {
          for (const item of this.newFiles) {
            uploaded.push(await DangersService.actions.uploadDangerPhoto(this.section.id, dangerId, user, item.file));
          }
          const danger = { ...this.form, images: [...this.keptImages, ...uploaded] };
          if (this.danger) {
            await DangersService.actions.updateDanger(dangerId, danger);
          } else {
            await DangersService.actions.createDanger(this.section, danger, user, dangerId);
          }
        } catch (error) {
          console.error("Error saving danger:", error);
          notifyError("Le danger n'a pas pu être enregistré. Réessayez.");
          // Don't leave orphan photos behind.
          await Promise.allSettled(uploaded.map((image) => DangersService.actions.deleteDangerPhoto(image.path)));
          this.saving = false;
          return;
        }

        // Removed photos are deleted once the danger no longer points to them.
        await Promise.allSettled(
          this.removedImages.map((image) => DangersService.actions.deleteDangerPhoto(image.path)),
        );
        this.saving = false;
        notify(this.danger ? "Danger modifié." : "Merci! Le danger a été signalé.");
        this.$emit("saved", dangerId);
        this.$emit("update:modelValue", false);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .photo-thumb {
    position: relative;
    width: 112px;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    background: rgb(var(--v-theme-surface-variant));

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }
  .photo-remove {
    position: absolute;
    top: 4px;
    right: 4px;
  }
</style>
